import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LogoutAuthOptions, OidcSecurityService } from 'angular-auth-oidc-client';
import { TablerIconComponent } from 'angular-tabler-icons';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from 'src/app/shared/services/layout.service';
import { environment } from '../../../../../environments/environment';

interface FooterMenuItem {
  icon: string;
  label: string;
  visible?: boolean;
  action: () => void;
}

/** DS-UserFooter: Avatar-Badge + nach oben öffnendes Menü + Hilfe-Icon-Button.
    Compact: Avatar/Hilfe gestapelt, Menü als weißes Popover rechts. */
@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [CommonModule, TablerIconComponent, TooltipModule],
  templateUrl: './user-menu.component.html',
  styleUrl: './user-menu.component.css',
})
export class UserMenuComponent {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly el = inject(ElementRef);
  readonly layoutService = inject(LayoutService);

  protected readonly open = signal(false);

  private readonly userData = toSignal(this.oidcSecurityService.userData$);
  protected readonly userName = computed(() => {
    const d: any = this.userData()?.userData;
    return d?.name || d?.preferred_username || d?.email || 'Benutzer';
  });
  protected readonly initials = computed(() => {
    const parts = String(this.userName()).trim().split(/\s+/).filter(Boolean);
    const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
    return letters || String(this.userName()).slice(0, 2).toUpperCase();
  });

  protected readonly menuItems: FooterMenuItem[] = [
    {
      icon: 'user',
      label: 'Profil',
      action: () => this.router.navigate(['/userprofile']),
    },
    // TODO(design-system): temporär – vor Merge nach main entfernen oder freischalten.
    {
      icon: 'palette',
      label: 'Design-System',
      visible: environment.showDesignSystem,
      action: () => this.router.navigate(['/design-system']),
    },
    {
      icon: 'logout',
      label: 'Abmelden',
      action: () => this.logout(),
    },
  ];
  protected readonly visibleMenuItems = this.menuItems.filter((i) => i.visible !== false);

  toggleMenu(): void {
    this.open.update((o) => !o);
  }

  pick(item: FooterMenuItem): void {
    this.open.set(false);
    item.action();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (this.open() && !this.el.nativeElement.contains(event.target)) {
      this.open.set(false);
    }
  }

  openNectaManual(): void {
    window.open('https://docu.necta.one/', '_blank');
  }

  /** Loggt den Nutzer aus und beendet die Sitzung. */
  logout(): void {
    const options: LogoutAuthOptions = {
      customParams: { state: 'local' },
    };
    this.oidcSecurityService.logoffAndRevokeTokens('', options).subscribe();
  }
}
